import { supabase } from '../../../../supabaseConfig';

/**
 * Serviço para gerenciamento de saldos de responsáveis - Nova estrutura database-driven
 * Agora usa as functions do banco e views otimizadas
 */
export class SaldosResponsaveisService {

     /**
      * Busca saldo de um responsável específico
      */
     static async getSaldoByResponsavel(organizationId: number, responsavelId: number) {
          try {
               const response = await supabase
                    .from('vw_saldos_responsaveis')
                    .select('*')
                    .eq('organizacao', organizationId)
                    .eq('id_responsavel', responsavelId)
                    .single();

               return response;
          } catch (error) {
               console.error('Erro ao buscar saldo do responsável:', error);
               throw error;
          }
     }

     /**
      * Adiciona crédito ao saldo do responsável usando function do banco
      */
     static async adicionarCredito(
          organizationId: number,
          responsavelId: number,
          valor: number,
          tipoPagamento: string,
          _userId?: number,
          numeroComprovante?: string,
          observacoes?: string
     ) {
          try {
               const response = await supabase.rpc('fn_baixa_manual_recarga_externa', {
                    p_id_responsavel: responsavelId,
                    p_valor: valor,
                    p_tipo_pagamento: tipoPagamento,
                    p_id_organizacao: organizationId,
                    p_created_by: _userId || responsavelId,
                    p_numero_comprovante: numeroComprovante,
                    p_observacoes: observacoes
               });

               return response;
          } catch (error) {
               console.error('Erro ao adicionar crédito:', error);
               throw error;
          }
     }

     /**
      * Debita valor do saldo do responsável
      */
     static async debitarSaldo(
          organizationId: number,
          responsavelId: number,
          valor: number,
          tipoOrigem: string,
          origemId: number,
          descricao: string,
          _userId?: number
     ) {
          try {
               // Buscar saldo atual
               const saldoAtual = await this.getSaldoByResponsavel(organizationId, responsavelId);
               
               if (saldoAtual.error || !saldoAtual.data) {
                    throw new Error('Responsável não possui saldo cadastrado');
               }

               if (!saldoAtual.data.saldo_atual || saldoAtual.data.saldo_atual < valor) {
                    throw new Error('Saldo insuficiente');
               }

               const novoSaldo = saldoAtual.data.saldo_atual - valor;

               // Criar movimentação de débito
               const response = await supabase
                    .from('movimentacoes_saldo')
                    .insert({
                         fk_id_pessoa: responsavelId,
                         fk_id_organizacao: organizationId,
                         tipo_movimentacao: 'debito',
                         tipo_origem: tipoOrigem,
                         origem_id: origemId,
                         valor: valor,
                         saldo_anterior: saldoAtual.data.saldo_atual,
                         saldo_posterior: novoSaldo,
                         descricao: descricao,
                         data_movimentacao: new Date().toISOString(),
                         created_by: _userId || responsavelId
                    })
                    .select()
                    .single();

               // Atualizar saldo
               if (!response.error) {
                    await supabase
                         .from('saldos_responsaveis')
                         .update({
                              saldo_atual: novoSaldo,
                              updated_at: new Date().toISOString()
                         })
                         .eq('fk_id_responsavel', responsavelId)
                         .eq('fk_id_organizacao', organizationId);
               }

               return response;
          } catch (error) {
               console.error('Erro ao debitar saldo:', error);
               throw error;
          }
     }

     /**
      * Transfere saldo do responsável para um aluno usando function do banco
      */
     static async transferirParaAluno(
          organizationId: number,
          responsavelId: number,
          alunoId: number,
          valor: number,
          userId?: number
     ) {
          try {
               const response = await supabase.rpc('fn_transferir_credito_aluno', {
                    p_id_responsavel: responsavelId,
                    p_id_aluno: alunoId,
                    p_valor: valor,
                    p_id_organizacao: organizationId,
                    p_created_by: userId || responsavelId
               });

               return response;
          } catch (error) {
               console.error('Erro ao transferir para aluno:', error);
               throw error;
          }
     }

     /**
      * Bloqueia valor no saldo (para transações pendentes)
      */
     static async bloquearSaldo(
          organizationId: number,
          responsavelId: number,
          valor: number,
          _userId?: number
     ) {
          try {
               const saldoAtual = await this.getSaldoByResponsavel(organizationId, responsavelId);
               
               if (saldoAtual.error || !saldoAtual.data) {
                    throw new Error('Responsável não possui saldo cadastrado');
               }

               const saldoDisponivel = (saldoAtual.data.saldo_atual || 0) - (saldoAtual.data.saldo_bloqueado || 0);

               if (saldoDisponivel < valor) {
                    throw new Error('Saldo disponível insuficiente');
               }

               const response = await supabase
                    .from('saldos_responsaveis')
                    .update({
                         saldo_bloqueado: (saldoAtual.data.saldo_bloqueado || 0) + valor,
                         updated_at: new Date().toISOString()
                    })
                    .eq('fk_id_responsavel', responsavelId)
                    .eq('fk_id_organizacao', organizationId)
                    .select();

               return response;
          } catch (error) {
               console.error('Erro ao bloquear saldo:', error);
               throw error;
          }
     }

     /**
      * Desbloqueia valor no saldo
      */
     static async desbloquearSaldo(
          organizationId: number,
          responsavelId: number,
          valor: number,
          _userId?: number
     ) {
          try {
               const saldoAtual = await this.getSaldoByResponsavel(organizationId, responsavelId);
               
               if (saldoAtual.error || !saldoAtual.data) {
                    throw new Error('Responsável não possui saldo cadastrado');
               }

               const response = await supabase
                    .from('saldos_responsaveis')
                    .update({
                         saldo_bloqueado: Math.max(0, (saldoAtual.data.saldo_bloqueado || 0) - valor),
                         updated_at: new Date().toISOString()
                    })
                    .eq('fk_id_responsavel', responsavelId)
                    .eq('fk_id_organizacao', organizationId)
                    .select();

               return response;
          } catch (error) {
               console.error('Erro ao desbloquear saldo:', error);
               throw error;
          }
     }

     /**
      * Busca todos os saldos com informações dos responsáveis
      */
     static async getAllWithResponsaveis(organizationId: number) {
          try {
               const response = await supabase
                    .from('vw_saldos_responsaveis')
                    .select('*')
                    .eq('organizacao', organizationId)
                    .order('saldo_atual', { ascending: false });

               return response;
          } catch (error) {
               console.error('Erro ao buscar todos os saldos:', error);
               throw error;
          }
     }

     /**
      * Relatório de movimentações por responsável
      */
     static async getRelatorioMovimentacoes(
          organizationId: number,
          responsavelId: number,
          dataInicio?: string,
          dataFim?: string
     ) {
          try {
               let query = supabase
                    .from('vw_historico_financeiro')
                    .select('*')
                    .eq('fk_id_organizacao', organizationId)
                    .eq('fk_id_pessoa', responsavelId)
                    .order('data_movimentacao', { ascending: false });

               if (dataInicio) {
                    query = query.gte('data_movimentacao', dataInicio);
               }

               if (dataFim) {
                    query = query.lte('data_movimentacao', dataFim);
               }

               return await query;
          } catch (error) {
               console.error('Erro ao buscar relatório de movimentações:', error);
               throw error;
          }
     }
}

export const saldosResponsaveisService = new SaldosResponsaveisService();
